import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, X, Search, Phone, Mail } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

const Header = () => {
  const { getCartCount } = useCart();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const cartCount = getCartCount();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Shop All', path: '/category/all' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm">
      {/* Top Bar */}
      <div className="bg-sky-500 text-white py-2 px-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center text-sm">
          <div className="hidden md:flex items-center gap-4">
            <a href="tel:+1-888-435-6822" className="flex items-center gap-1 hover:text-sky-100 transition-colors">
              <Phone size={14} />
              <span>+1-888-435-6822</span>
            </a>
            <a href="mailto:support@cartlixs.store" className="flex items-center gap-1 hover:text-sky-100 transition-colors">
              <Mail size={14} />
              <span>support@cartlixs.store</span>
            </a>
          </div>
          <p className="text-center flex-1 md:flex-none font-medium">
            Free Shipping on Orders Over $50!
          </p>
          <div className="hidden md:block"></div>
        </div>
      </div>

      {/* Main Header */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <h1 className="text-2xl md:text-3xl font-bold text-sky-500" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Cartlixs
            </h1>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="text-slate-700 hover:text-sky-500 font-medium transition-colors"
                style={{ fontFamily: 'Quicksand, sans-serif' }}
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Cart & Mobile Menu */}
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              className="relative p-2 hover:bg-sky-50"
              onClick={() => navigate('/cart')}
              data-testid="cart-button"
            >
              <ShoppingCart className="h-6 w-6 text-slate-700" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-orange-400 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Button>

            {/* Mobile Menu */}
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] bg-white">
                <div className="flex flex-col gap-6 mt-8">
                  <Link to="/" className="mb-4">
                    <h1 className="text-2xl font-bold text-sky-500" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                      Cartlixs
                    </h1>
                  </Link>
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="text-lg text-slate-700 hover:text-sky-500 font-medium transition-colors"
                      style={{ fontFamily: 'Quicksand, sans-serif' }}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {link.name}
                    </Link>
                  ))}
                  <div className="border-t pt-4 mt-4">
                    <a href="tel:+1-888-435-6822" className="flex items-center gap-2 text-slate-600 mb-3">
                      <Phone size={18} />
                      <span>+1-888-435-6822</span>
                    </a>
                    <a href="mailto:support@cartlixs.store" className="flex items-center gap-2 text-slate-600">
                      <Mail size={18} />
                      <span>support@cartlixs.store</span>
                    </a>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
